import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, ArrowRight, Check, Sparkles, Car, Truck, Calendar as CalendarIcon,
  Clock, MapPin, User, Mail, Phone, Loader2, PartyPopper,
} from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { SERVICES, VEHICLE_SIZES, HOURS, BLACKOUT_MONTHS, OPEN_EXCEPTIONS, slotsForDate, priceFor, BUSINESS } from "@/lib/data";
import { createBooking, fetchUnavailableDates } from "@/lib/api";

const STEPS = [
  { key: "service", label: "Service" },
  { key: "vehicle", label: "Vehicle" },
  { key: "datetime", label: "Date & Time" },
  { key: "contact", label: "Contact" },
  { key: "confirm", label: "Confirm" },
];

const formatDate = (date) => {
  if (!date) return "";
  return date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
};

const toISODate = (date) => {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export default function BookingPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [booking, setBooking] = useState({
    serviceId: "",
    vehicleSize: "",
    petHair: false,
    date: null,
    time: "",
    fullName: "",
    email: "",
    phone: "",
    address: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [unavailable, setUnavailable] = useState(new Set());

  useEffect(() => {
    fetchUnavailableDates()
      .then((dates) => setUnavailable(new Set(dates)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const preselect = params.get("service");
    if (preselect && SERVICES.find((s) => s.id === preselect)) {
      setBooking((b) => ({ ...b, serviceId: preselect }));
    }
  }, [params]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  const service = SERVICES.find((s) => s.id === booking.serviceId);
  const vehicle = VEHICLE_SIZES.find((v) => v.id === booking.vehicleSize);
  const basePrice = service && vehicle ? priceFor(service, vehicle.id) : 0;
  const petHairFee = booking.petHair ? 10 : 0;
  const total = basePrice + petHairFee;

  const canAdvance = () => {
    if (step === 0) return !!booking.serviceId;
    if (step === 1) return !!booking.vehicleSize;
    if (step === 2) return !!booking.date && !!booking.time;
    if (step === 3) {
      return (
        booking.fullName.trim().length > 1 &&
        /\S+@\S+\.\S+/.test(booking.email) &&
        booking.phone.replace(/\D/g, "").length >= 10 &&
        booking.address.trim().length > 4
      );
    }
    return true;
  };

  const submit = async () => {
    if (!service || !vehicle || !booking.date || !booking.time) return;
    setSubmitting(true);
    try {
      const payload = {
        service_id: service.id,
        service_name: service.name,
        vehicle_size: vehicle.id,
        price: total,
        pet_hair: booking.petHair,
        date: toISODate(booking.date),
        time: booking.time,
        full_name: booking.fullName,
        email: booking.email,
        phone: booking.phone,
        address: booking.address,
        notes: booking.notes,
      };
      const res = await createBooking(payload);
      setConfirmed(res);
      toast.success("Booking confirmed! Check your email.");
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409) {
        toast.error(detail || "That day is already booked. Please pick another date.");
        // Refresh unavailable dates and bump back to date step
        fetchUnavailableDates().then((dates) => setUnavailable(new Set(dates))).catch(() => {});
        setStep(2);
      } else {
        toast.error("Couldn't submit booking. Please text us at " + BUSINESS.phone);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmed) {
    return (
      <main className="min-h-[100svh] hero-gradient pt-28 pb-20" data-testid="booking-confirmed">
        <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="text-center"
          >
            <div className="inline-flex w-20 h-20 rounded-full bg-[#7CB342]/20 border-2 border-[#7CB342] items-center justify-center mb-6">
              <PartyPopper size={36} className="text-[#7CB342]" />
            </div>
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight text-[#F5F1E8]">
              You're booked!
            </h1>
            <p className="mt-4 text-lg text-[#9CA3AF]">
              We sent a confirmation to <span className="text-[#F5F1E8]">{confirmed.email}</span>.
              We'll text {confirmed.phone} the morning of to confirm arrival time.
            </p>

            <div className="mt-8 rounded-2xl border border-white/10 bg-[#111827] p-6 text-left space-y-2.5">
              <Row k="Service" v={confirmed.service_name} />
              <Row k="Vehicle" v={VEHICLE_SIZES.find(v => v.id === confirmed.vehicle_size)?.label} />
              <Row k="Date" v={confirmed.date} />
              <Row k="Time" v={confirmed.time} />
              <Row k="Address" v={confirmed.address} />
              {confirmed.pet_hair && <Row k="Add-on" v="Heavy pet hair (+$10)" />}
              <Row k="Total" v={`$${confirmed.price}`} highlight />
              <div className="pt-3 border-t border-white/5 text-xs text-[#9CA3AF]">
                Booking ID: <span className="text-[#F5F1E8]/70">{confirmed.id}</span>
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link to="/" className="btn-ghost-cream rounded-full px-6 py-3 text-sm" data-testid="confirmed-home-btn">
                Back to Home
              </Link>
              <a href={BUSINESS.phoneHref} className="btn-pickle rounded-full px-6 py-3 text-sm" data-testid="confirmed-call-btn">
                Call / Text Us
              </a>
            </div>
          </motion.div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-[100svh] hero-gradient pt-24 sm:pt-28 pb-24" data-testid="booking-page">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <div className="text-center mb-10">
          <div className="label-eyebrow mb-3">Book a Detail</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-[#F5F1E8]">
            Let's get your car shining.
          </h1>
        </div>

        {/* Stepper */}
        <div className="mb-10">
          <div className="flex items-center justify-between gap-1 sm:gap-2">
            {STEPS.map((s, i) => (
              <div key={s.key} className="flex-1 flex items-center gap-1 sm:gap-2">
                <div
                  className={`flex-shrink-0 w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                    i < step
                      ? "bg-[#7CB342] text-[#0A0E17]"
                      : i === step
                      ? "bg-[#7CB342] text-[#0A0E17] ring-4 ring-[#7CB342]/20"
                      : "bg-[#1F2937] text-[#9CA3AF]"
                  }`}
                  data-testid={`step-indicator-${i}`}
                >
                  {i < step ? <Check size={15} /> : i + 1}
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 rounded-full transition-colors ${i < step ? "bg-[#7CB342]" : "bg-white/10"}`} />
                )}
              </div>
            ))}
          </div>
          <div className="mt-3 flex justify-between text-[10px] sm:text-xs tracking-[0.18em] uppercase font-bold text-[#9CA3AF]">
            {STEPS.map((s, i) => (
              <span key={s.key} className={i === step ? "text-[#7CB342]" : ""}>
                {s.label}
              </span>
            ))}
          </div>
        </div>

        {/* Step content */}
        <div className="rounded-3xl border border-white/8 bg-[#111827] p-6 sm:p-9">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {step === 0 && <StepService booking={booking} setBooking={setBooking} />}
              {step === 1 && <StepVehicle booking={booking} setBooking={setBooking} />}
              {step === 2 && <StepDateTime booking={booking} setBooking={setBooking} unavailable={unavailable} />}
              {step === 3 && <StepContact booking={booking} setBooking={setBooking} />}
              {step === 4 && <StepConfirm booking={booking} service={service} vehicle={vehicle} basePrice={basePrice} petHairFee={petHairFee} total={total} />}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Navigation */}
        <div className="mt-6 flex items-center justify-between gap-3">
          <button
            onClick={() => (step === 0 ? navigate("/") : setStep((s) => s - 1))}
            data-testid="booking-back-btn"
            className="btn-ghost-cream inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm"
          >
            <ArrowLeft size={15} />
            {step === 0 ? "Cancel" : "Back"}
          </button>

          {step < STEPS.length - 1 ? (
            <button
              onClick={() => canAdvance() && setStep((s) => s + 1)}
              disabled={!canAdvance()}
              data-testid="booking-next-btn"
              className="btn-pickle inline-flex items-center gap-2 rounded-full px-7 py-3 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continue
              <ArrowRight size={15} />
            </button>
          ) : (
            <button
              onClick={submit}
              disabled={submitting}
              data-testid="booking-confirm-btn"
              className="btn-pickle inline-flex items-center gap-2 rounded-full px-7 py-3 text-sm disabled:opacity-60"
            >
              {submitting ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
              {submitting ? "Booking..." : "Confirm Booking"}
            </button>
          )}
        </div>
      </div>
    </main>
  );
}

const Row = ({ k, v, highlight }) => (
  <div className="flex justify-between gap-4 text-sm">
    <span className="text-[#9CA3AF]">{k}</span>
    <span className={`text-right ${highlight ? "text-[#7CB342] font-display font-bold text-lg" : "text-[#F5F1E8]"}`}>{v}</span>
  </div>
);

// ---------- STEP 1: Service ----------
const StepService = ({ booking, setBooking }) => (
  <div>
    <h2 className="font-display font-bold text-2xl text-[#F5F1E8] mb-1">Pick your service</h2>
    <p className="text-sm text-[#9CA3AF] mb-6">Choose what your car needs.</p>
    <div className="space-y-3">
      {SERVICES.map((s) => {
        const active = booking.serviceId === s.id;
        return (
          <button
            key={s.id}
            onClick={() => setBooking({ ...booking, serviceId: s.id })}
            data-testid={`step-service-${s.id}`}
            className={`w-full text-left rounded-2xl border p-5 transition-all ${
              active
                ? "border-[#7CB342] bg-[#7CB342]/10 ring-2 ring-[#7CB342]/30"
                : "border-white/10 bg-[#0A0E17] hover:border-white/25"
            }`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles size={14} className="text-[#7CB342]" />
                  <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#9CA3AF]">{s.duration}</span>
                  {s.popular && (
                    <span className="bg-[#E53935] text-white text-[9px] tracking-wider font-bold uppercase px-2 py-0.5 rounded-full">Popular</span>
                  )}
                </div>
                <div className="font-display font-bold text-lg text-[#F5F1E8]">{s.name}</div>
                <div className="text-sm text-[#9CA3AF] mt-1">{s.blurb}</div>
              </div>
              <div className="flex flex-col items-end">
                <div className="text-xs text-[#9CA3AF]">From</div>
                <div className="font-display font-extrabold text-2xl text-[#7CB342]">${Math.min(...Object.values(s.prices))}</div>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  </div>
);

// ---------- STEP 2: Vehicle Size ----------
const StepVehicle = ({ booking, setBooking }) => {
  const service = SERVICES.find((s) => s.id === booking.serviceId);
  return (
    <div>
      <h2 className="font-display font-bold text-2xl text-[#F5F1E8] mb-1">Vehicle size</h2>
      <p className="text-sm text-[#9CA3AF] mb-6">Helps us bring the right gear and quote your exact price.</p>
      <div className="grid sm:grid-cols-3 gap-3">
        {VEHICLE_SIZES.map((v) => {
          const Icon = v.id === "large" ? Truck : Car;
          const active = booking.vehicleSize === v.id;
          const price = service ? priceFor(service, v.id) : 0;
          return (
            <button
              key={v.id}
              onClick={() => setBooking({ ...booking, vehicleSize: v.id })}
              data-testid={`step-vehicle-${v.id}`}
              className={`rounded-2xl border p-5 text-left transition-all ${
                active
                  ? "border-[#7CB342] bg-[#7CB342]/10 ring-2 ring-[#7CB342]/30"
                  : "border-white/10 bg-[#0A0E17] hover:border-white/25"
              }`}
            >
              <Icon size={28} className={active ? "text-[#7CB342]" : "text-[#9CA3AF]"} />
              <div className="font-display font-bold text-base text-[#F5F1E8] mt-4">{v.label}</div>
              {service && (
                <div className="mt-3 pt-3 border-t border-white/5">
                  <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#9CA3AF]">Total</div>
                  <div className="font-display font-extrabold text-xl text-[#7CB342]">${price}</div>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Pet hair add-on toggle */}
      <button
        type="button"
        onClick={() => setBooking({ ...booking, petHair: !booking.petHair })}
        data-testid="step-pet-hair-toggle"
        className={`mt-5 w-full rounded-2xl border p-5 flex items-start sm:items-center gap-4 text-left transition-all ${
          booking.petHair
            ? "border-[#7CB342] bg-[#7CB342]/10 ring-2 ring-[#7CB342]/30"
            : "border-white/10 bg-[#0A0E17] hover:border-white/25"
        }`}
        aria-pressed={booking.petHair}
      >
        <span className="text-3xl select-none" aria-hidden="true">🐾</span>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-display font-bold text-base text-[#F5F1E8]">Heavy pet hair add-on</span>
            <span className="bg-[#7CB342] text-[#0A0E17] text-[10px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full">+$10</span>
          </div>
          <p className="mt-1 text-sm text-[#9CA3AF]">
            Toggle on if your car has heavy pet hair. We'll bring extra extraction tools and lint blocks.
          </p>
        </div>
        <span
          className={`flex-shrink-0 w-11 h-6 rounded-full p-0.5 transition-colors ${
            booking.petHair ? "bg-[#7CB342]" : "bg-white/15"
          }`}
        >
          <span
            className={`block w-5 h-5 rounded-full bg-white shadow transition-transform ${
              booking.petHair ? "translate-x-5" : "translate-x-0"
            }`}
          />
        </span>
      </button>
    </div>
  );
};

// ---------- STEP 3: Date & Time ----------
const StepDateTime = ({ booking, setBooking, unavailable }) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const isClosedDay = (d) => HOURS[d.getDay()].closed;
  const isBooked = (d) => unavailable && unavailable.has(toISODate(d));
  const isBlackout = (d) => BLACKOUT_MONTHS.includes(d.getMonth() + 1);
  const isException = (d) => OPEN_EXCEPTIONS.includes(toISODate(d));
  const isDisabled = (d) => {
    if (d < today) return true;
    if (isBooked(d)) return true;
    // Explicit open exceptions override closed-day + blackout rules
    if (isException(d)) return false;
    return isClosedDay(d) || isBlackout(d);
  };

  // Available start times depend on the selected date.
  const availableSlots = slotsForDate(booking.date);

  return (
    <div>
      <h2 className="font-display font-bold text-2xl text-[#F5F1E8] mb-1">Pick a date & time</h2>
      <p className="text-sm text-[#9CA3AF] mb-6">We'll confirm the exact arrival window via text.</p>

      <div className="mb-5 rounded-xl border border-[#7CB342]/30 bg-[#7CB342]/10 p-4 flex items-start gap-3 text-sm text-[#F5F1E8]/90" data-testid="one-per-day-notice">
        <Sparkles size={16} className="text-[#7CB342] mt-0.5 flex-shrink-0" />
        <div>
          <strong className="text-[#7CB342]">Only one vehicle per day.</strong>{" "}
          Dillpickle's is a one-person mobile detailing business — booked dates are dimmed.
          Hours: Mon–Thu 2–6 PM · Sat 8 AM–2 PM · Closed Fri &amp; Sun.{" "}
          <strong className="text-[#E53935]">No bookings in July — owner is away.</strong>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-white/10 bg-[#0A0E17] p-4">
          <Calendar
            mode="single"
            selected={booking.date}
            onSelect={(d) => setBooking({ ...booking, date: d, time: "" })}
            disabled={isDisabled}
            data-testid="booking-calendar"
            className="text-[#F5F1E8]"
          />
        </div>
        <div>
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#9CA3AF] mb-3">
            {booking.date ? formatDate(booking.date) : "Select a date first"}
          </div>
          {booking.date && ["2026-07-31"].includes(toISODate(booking.date)) && (
            <div className="text-xs text-[#9CA3AF] mb-3" data-testid="friday-hours-note">
              Morning-only this day (8 AM – 12 PM).
            </div>
          )}
          <div className="grid grid-cols-3 gap-2">
            {availableSlots.map((t) => {
              const active = booking.time === t;
              return (
                <button
                  key={t}
                  onClick={() => booking.date && setBooking({ ...booking, time: t })}
                  disabled={!booking.date}
                  data-testid={`step-time-${t.replace(/[: ]/g, "")}`}
                  className={`py-2.5 rounded-full text-xs font-semibold transition-all ${
                    active
                      ? "bg-[#7CB342] text-[#0A0E17]"
                      : "bg-[#0A0E17] border border-white/10 text-[#F5F1E8]/85 hover:border-[#7CB342]/40 disabled:opacity-40 disabled:cursor-not-allowed"
                  }`}
                >
                  {t}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const ContactField = ({ label, icon: Icon, ...props }) => (
  <div>
    <label className="text-xs tracking-[0.2em] uppercase font-bold text-[#9CA3AF] flex items-center gap-1.5">
      <Icon size={13} /> {label}
    </label>
    <Input
      {...props}
      className="mt-2 bg-[#0A0E17] border-white/10 focus-visible:border-[#7CB342] focus-visible:ring-[#7CB342]/30 text-[#F5F1E8]"
    />
  </div>
);

// ---------- STEP 4: Contact ----------
const StepContact = ({ booking, setBooking }) => {
  return (
    <div>
      <h2 className="font-display font-bold text-2xl text-[#F5F1E8] mb-1">Your details</h2>
      <p className="text-sm text-[#9CA3AF] mb-6">Where to come and how to reach you.</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <ContactField
          label="Full Name"
          icon={User}
          value={booking.fullName}
          onChange={(e) => setBooking({ ...booking, fullName: e.target.value })}
          placeholder="Jane Doe"
          data-testid="booking-name-input"
        />
        <ContactField
          label="Phone"
          icon={Phone}
          type="tel"
          value={booking.phone}
          onChange={(e) => setBooking({ ...booking, phone: e.target.value })}
          placeholder="(404) 555-1234"
          data-testid="booking-phone-input"
        />
        <div className="sm:col-span-2">
          <ContactField
            label="Email"
            icon={Mail}
            type="email"
            value={booking.email}
            onChange={(e) => setBooking({ ...booking, email: e.target.value })}
            placeholder="you@email.com"
            data-testid="booking-email-input"
          />
        </div>
        <div className="sm:col-span-2">
          <ContactField
            label="Service Address"
            icon={MapPin}
            value={booking.address}
            onChange={(e) => setBooking({ ...booking, address: e.target.value })}
            placeholder="123 Peachtree St, Atlanta, GA 30309"
            data-testid="booking-address-input"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs tracking-[0.2em] uppercase font-bold text-[#9CA3AF]">Notes (optional)</label>
          <Textarea
            rows={3}
            value={booking.notes}
            onChange={(e) => setBooking({ ...booking, notes: e.target.value })}
            placeholder="Pet hair, specific concerns, gate code..."
            data-testid="booking-notes-input"
            className="mt-2 bg-[#0A0E17] border-white/10 focus-visible:border-[#7CB342] focus-visible:ring-[#7CB342]/30 text-[#F5F1E8]"
          />
        </div>
      </div>
    </div>
  );
};

// ---------- STEP 5: Confirm ----------
const StepConfirm = ({ booking, service, vehicle, basePrice, petHairFee, total }) => (
  <div data-testid="step-confirm">
    <h2 className="font-display font-bold text-2xl text-[#F5F1E8] mb-1">Review & confirm</h2>
    <p className="text-sm text-[#9CA3AF] mb-6">Make sure everything looks right before we lock it in.</p>
    <div className="rounded-2xl border border-white/10 bg-[#0A0E17] p-5 sm:p-6 space-y-3">
      <Row k="Service" v={service?.name} />
      <Row k="Duration" v={service?.duration} />
      <Row k="Vehicle Size" v={vehicle?.label} />
      <Row k="Date" v={formatDate(booking.date)} />
      <Row k="Time" v={booking.time} />
      <div className="border-t border-white/5 my-2" />
      <Row k="Name" v={booking.fullName} />
      <Row k="Email" v={booking.email} />
      <Row k="Phone" v={booking.phone} />
      <Row k="Address" v={booking.address} />
      {booking.notes && <Row k="Notes" v={booking.notes} />}
      <div className="border-t border-white/5 my-2" />
      <Row k={`${service?.name || 'Package'} (${vehicle?.short || ''})`} v={`$${basePrice}`} />
      {petHairFee > 0 && <Row k="Heavy pet hair add-on" v={`+$${petHairFee}`} />}
      <div className="border-t border-white/5 my-2" />
      <Row k="Total Due (on completion)" v={`$${total}`} highlight />
    </div>
    <div className="mt-5 rounded-xl border border-[#7CB342]/30 bg-[#7CB342]/8 p-4 flex items-start gap-3 text-sm text-[#F5F1E8]/85">
      <Sparkles size={18} className="text-[#7CB342] mt-0.5 flex-shrink-0" />
      <div>
        <strong className="text-[#7CB342]">Satisfaction Guaranteed.</strong> No deposit required.
        Pay on-site after the detail. We'll text you the morning of to confirm.
      </div>
    </div>
  </div>
);
